import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Phone } from "lucide-react";
import heroImage from "@/assets/concrete-paver-patio-installation-totowa-nj-hero.webp";
import crackInspectionImage from "@/assets/concrete-patio-crack-inspection-homeowner-totowa-nj.webp";
import controlJointImage from "@/assets/concrete-patio-control-joint-tooling-detail-totowa-nj.webp";
import finishedSlabImage from "@/assets/concrete-patio-finished-slab-result-totowa-nj.webp";

const faqs = [
  {
    question: "How soon can you get out to look at my patio?",
    answer:
      "We start with a site visit where we walk your yard and check the grade before we ever schedule a pour. This step matters in Totowa because clay soil hides problems that aren't visible from the surface. We look at how water moves across your yard right now, not just where the cracks are. That visit tells us whether you need a full replacement or a smaller repair. Skipping this step is how patios fail early, so we never rush past it.",
  },
  {
    question: "What should I expect on installation day?",
    answer:
      "On installation day, you'll see excavation, base prep, forms, and the pour happen in stages, not all at once. We dig out the old soil, compact a gravel base, set forms and reinforcement, then pour and finish the concrete. The pour itself moves fast once we start, but the steps before it take the most time. If a crew skips straight to pouring without base work, that's a warning sign your patio won't hold up through a Totowa winter.",
  },
  {
    question: "Why do patios crack or sink in Totowa specifically?",
    answer:
      "Totowa's heavy clay soil swells when wet and shrinks when dry, and repeated freeze-thaw cycles from December through March make it worse. That movement pushes slabs up unevenly or lets them sink where the base wasn't compacted right. We see this every week: a patio looks fine for a couple years, then cracks along one edge. It's almost always a base problem, not a concrete problem. That's why proper depth and compaction matter more here than in drier climates.",
  },
  {
    question: "Do I need a full patio replacement, or can it be repaired?",
    answer:
      "Not always. Isolated hairline cracks, small chips, or minor settling under half an inch can often be repaired instead of replaced. We patch plenty of small settling spots and send homeowners on their way without a full tear-out. But if cracks run in different directions across the whole slab, or heaving shows up in multiple spots, the base underneath has likely failed. Patching one crack in that case just means another shows up nearby next winter.",
  },
  {
    question: "How long before I can use my new patio?",
    answer:
      "Plan on about a week before you walk on the concrete regularly, and longer before furniture or grills go back on it. Curing time protects the slab's strength, so rushing it shortens the life of your patio. We add control joints during finishing so the concrete has a planned place to crack instead of cracking wherever it wants. Waiting the full cure time is a small trade-off for a patio that holds up through Totowa's freeze-thaw seasons.",
  },
  {
    question: "Is there a best time of year to install a concrete patio in Totowa?",
    answer:
      "Spring and fall usually give the most predictable conditions for pouring and curing concrete in Totowa. Extreme summer heat can dry the surface too fast, and winter's freeze-thaw cycle is exactly what damages patios with a weak base. Scheduling around milder weather gives the concrete time to cure properly before it faces its first freeze. If you're planning ahead, booking before the ground freezes gives us more flexibility with excavation and base prep.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

export default function ConcretePatios() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Concrete Patio Installation Totowa, NJ | Masonry Pro</title>
        <meta property="og:description" content="Concrete patio installation in Totowa. We handle base prep and drainage for North Jersey's clay soil and freeze-thaw winters. Free estimates, 35+ years." />
        <meta property="og:title" content="Concrete Patio Installation Totowa, NJ | Masonry Pro" />
        <meta
          name="description"
          content="Concrete patio installation in Totowa. We handle base prep and drainage for North Jersey's clay soil and freeze-thaw winters. Free estimates, 35+ years."
        />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Concrete Patio Installation",
            description: "Concrete patio installation in Totowa built with base prep and drainage for North Jersey's clay soil and freeze-thaw winters.",
            url: "/concrete-services/patios",
            image: "https://masonry-pro.com/og/concrete-general.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/concrete-general.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/concrete-general.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Concrete patio with paver border and fire pit installed behind a home in Totowa, NJ" width={1200} height={686} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Concrete Patio Installation in Totowa: <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Built to Outlast North Jersey Winters</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  A concrete patio needs to handle more than backyard barbecues here. Totowa's clay soil and freeze-thaw winters test any slab that isn't built right from the base up. We pour patios across Passaic, Bergen, Essex, and Morris counties with the base prep, reinforcement, and control joints that keep them flat for decades.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="secondary" size="xl" asChild>
                    <a href="tel:+19737252395" className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      (973) 725-2395
                    </a>
                  </Button>
                  <Button variant="goldOutline" size="xl" asChild>
                    <a href="#contact">Get Free Estimate</a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Section 1 */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Cracked, Sinking, or Heaving: <span className="text-gradient-gold">Diagnosing What Your Patio Needs</span>
                </h2>

                <div className="mb-8 p-8 bg-muted/50 rounded-xl border border-border">
                  <h3 className="text-xl font-bold mb-3">Quick Summary</h3>
                  <p className="text-lg text-muted-foreground">
                    Not every cracked patio needs a full replacement, but sinking, heaving, or wide gaps near the house usually point to a base or drainage problem underneath. We look at the pattern of damage first, then decide whether it's a patch job or a tear-out.
                  </p>
                </div>

                <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-left lg:mr-8">
                  <img
                    src={crackInspectionImage}
                    alt="Homeowner inspecting a cracked concrete patio near a sliding door in Totowa, NJ"
                    width={1024}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto rounded-xl border border-border"
                  />
                </figure>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Concrete cracks. That's not a defect, it's just what concrete does over time. Hairline cracks running in a straight line are usually nothing to worry about.
                  </p>
                  <p>
                    Sinking is a different story. If one section of your patio has dropped an inch or two below the rest, that's not a surface problem. The ground underneath is failing to hold weight, usually because the base wasn't compacted right when it went in, or water has been washing soil out from below for years.
                  </p>
                  <p>
                    Totowa sits on a lot of clay-heavy soil, and clay does something annoying: it swells when it's wet and shrinks when it dries out. Run that cycle every season for a few decades and you get heaving, where slabs tilt or lift unevenly instead of settling flat.
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Hairline surface cracks with no height difference between the pieces, usually cosmetic, can often be left alone or sealed</li>
                    <li>A crack you can slide a nickel into, or one edge higher than the other, a sign of base movement worth a real look</li>
                    <li>A slab that's dropped near the house foundation, often a drainage issue pushing water where it shouldn't be</li>
                    <li>Widespread heaving across the whole patio, not just one spot, points to soil movement under the entire slab</li>
                  </ul>
                  <p>
                    So how do you know which one you've got? Grab a garden hose, run water across the patio, and watch where it pools. If it collects right against your house instead of running off, you've found your real problem, and it's not the concrete.
                  </p>
                </div>
                <div className="clear-both" />
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Why Totowa's Clay Soil and <span className="text-gradient-gold">Freeze-Thaw Cycles</span> Test Every Patio
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Here's something nobody tells you when you're picking out patio material: the dirt under your yard matters more than the concrete on top of it.
                  </p>
                  <p>
                    Totowa sits on heavy clay soil, and clay does something annoying. It holds water like a sponge, then swells. When winter hits and that wet clay freezes, it expands and pushes upward. We call this frost heave. Come spring, the ground thaws and drops back down, but your patio doesn't drop evenly with it. That's how you end up with cracks, sunken corners, and slabs that tilt toward the house instead of away from it.
                  </p>
                  <p>
                    We see this every single week. Someone calls about a patio that looked fine for two or three years, then suddenly started cracking along one edge. It's almost always the same cause: the base under that slab wasn't built to handle a freeze-thaw cycle, or the soil underneath never got compacted right in the first place.
                  </p>
                  <p>
                    North Jersey winters aren't gentle. We get repeated freeze and thaw swings from December through March, sometimes several cycles in a single week. Add road salt runoff from nearby driveways and walkways, and you've got a second problem eating away at the surface from above while the clay pushes from below.
                  </p>
                  <p>
                    This is the whole reason base prep isn't a step you skip to save time. A patio poured over compacted stone, at the right depth for our frost line, shrugs off what a shallow, rushed base cannot. Skip that step and you're not fixing a design flaw later, you're fixing a foundation flaw.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  From Site Visit to Finished Slab: <span className="text-gradient-gold">The Concrete Patio Installation Process</span>
                </h2>
                <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-right lg:ml-8">
                  <img
                    src={controlJointImage}
                    alt="Tooling a control joint into fresh concrete during patio installation in Totowa, NJ"
                    width={1024}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto rounded-xl border border-border"
                  />
                </figure>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    So what happens once you call us? A lot of homeowners picture a crew showing up and pouring concrete the same day. It doesn't work that way, and if a contractor tries to skip straight to the pour, that's a red flag.
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Site visit. We walk your yard, check the grade, and look at how water moves across the area right now.</li>
                    <li>Excavation. We dig out the old soil down to a proper depth. This matters more here than in most places because Totowa sits on a lot of heavy clay.</li>
                    <li>Base prep. We lay and compact a gravel sub-base, the layer under the slab that lets water drain instead of pooling and freezing under the concrete.</li>
                    <li>Forms and reinforcement. We set wood forms to shape the patio, then place wire mesh or rebar so the slab holds together under stress.</li>
                    <li>The pour. Concrete goes in, gets screeded flat, and floated smooth. This part moves fast once we start.</li>
                    <li>Finish and joints. We add control joints to give the concrete a place to crack on purpose, instead of cracking wherever it wants.</li>
                  </ul>
                  <p>
                    Curing takes about a week before you should walk on it regularly, and longer before furniture or grills go back on. We've had folks in Totowa call us in a panic over hairline cracks that showed up in year two, and almost every time it traces back to a base that wasn't compacted right the first time. Do it once, do it right.
                  </p>
                </div>
                <div className="clear-both" />
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  When Repair Beats a <span className="text-gradient-gold">Full Patio Replacement</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Not every cracked patio needs to come out. We say this at estimates all the time, and homeowners are usually surprised to hear a mason talk them out of the bigger job.
                  </p>
                  <p>
                    If the cracks are isolated, the slab is mostly flat, and water isn't pooling underneath, a concrete repair can hold for years. We've patched plenty of small settling spots and sent people on their way.
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>One or two hairline cracks with no height difference across them</li>
                    <li>A corner or edge that chipped from a planter or salt exposure, but the rest of the slab is solid</li>
                    <li>Minor surface flaking, called spalling, without deep cracking underneath</li>
                    <li>A small section that settled less than half an inch, with drainage still working fine</li>
                  </ul>
                  <p>
                    Now, if the whole slab is heaving in different spots, that's a different story. Multiple cracks running in different directions usually mean the base underneath has failed, not just the surface.
                  </p>
                  <p>
                    Clay soil is common throughout Totowa lots, and it expands and contracts hard with the freeze-thaw cycle. Once that base starts moving, patching one crack just means the next one shows up six feet away next winter.
                  </p>
                  <p>
                    So here's the honest test. Stand at one edge of your patio and look across the surface at a low angle. See wide gaps, more than three separate cracks, or spots where water sits after rain instead of draining? That's not a patch job anymore, that's a base problem, and a <Link to="/concrete-services/repair" className="text-secondary hover:underline">repair</Link> will just buy you a season or two before you're back to square one.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  What Drives Concrete Patio <span className="text-gradient-gold">Pricing Up or Down</span>
                </h2>
                <figure className="mb-6 lg:mb-4 lg:w-1/2 lg:float-left lg:mr-8">
                  <img
                    src={finishedSlabImage}
                    alt="Finished concrete patio slab with outdoor dining set behind a Totowa, NJ home"
                    width={1024}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto rounded-xl border border-border"
                  />
                </figure>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    No two patios cost the same to build. That's not a sales line, it's just how this trade works. Two backyards the same size can price out completely differently once we get a shovel in the ground.
                  </p>
                  <p>
                    Soil is the big one around here. Totowa sits on clay-heavy soil in a lot of spots, and clay holds water. Hold water in the wrong spot and freeze-thaw cycles will heave a slab right out of the ground. So if your yard needs extra excavation or a thicker gravel base to handle that, the job takes more time and more material. That's not us padding a bill, that's us keeping your patio flat five winters from now.
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Size and shape of the patio, plus how many curves or cutouts you want</li>
                    <li>How deep we need to dig before we hit stable soil</li>
                    <li>Whether an old slab or patio needs to come out first</li>
                    <li>Drainage work, like grading or a drain line, to move water away from your foundation</li>
                    <li>Site access, since tight side yards or fenced-in backyards slow equipment down</li>
                    <li>Finish choice, since a plain broom finish costs less labor than decorative work</li>
                  </ul>
                  <p>
                    We get asked about <Link to="/concrete-services/stamped-concrete" className="text-secondary hover:underline">stamped concrete</Link> a lot. Even so, we steer most North Jersey homeowners toward pavers instead. Stamped concrete is one big poured slab, and when clay soil shifts or water gets under it, the whole thing can crack as one piece. Pavers flex individually and settle better through freeze-thaw. It's not about pushing a fancier product, it's about what holds up here.
                  </p>
                  <p>
                    The honest way to know your real number is a walk of the site.
                  </p>
                </div>
                <div className="clear-both" />
              </div>
            </div>
          </section>

          {/* Category hub backlink */}
          <section className="py-8 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-muted-foreground">
                  A concrete patio is one option among <Link to="/concrete-services" className="text-secondary hover:underline">the rest of our concrete work</Link> in North Jersey.
                </p>
                <p className="text-muted-foreground mt-4">
                  If a patio's showing damage instead of needing something new, <Link to="/concrete-services/repair" className="text-secondary hover:underline">concrete repair</Link> is often the smaller fix.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-background p-6 rounded-xl border border-border">
                    <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          {/* CTA Section */}
          <CTASection serviceName="Concrete Patio" heading="Ready for a New" highlightedHeading="Patio?" />
        </main>

        <Footer />
      </div>
    </>
  );
}
